import { useState } from "react";
import BaseModal from "@components/modals/BaseModal";
import CardSelection from "@components/ui/selection/CardSelection";
import DatePicker from "@components/ui/selection/DatePicker";
import Button from "@components/ui/buttons/Button";
import Icon1 from "@assets/icons/Icon";
import { Planning, PlanningModel, PlanningSection } from "types/planning";
import { PartnerInfos, Project, ProjectDocument, ProjectFolder, ProjectSubFolder } from "types";
import { PLANNING_SECTIONS } from "../../utils/planning_constants";
import { planningsApi } from "@services/api/plannings";
import { projectsApi } from "@services/api/projects";
import { showNotification } from "context/notification.service";
import { useProjectStore } from "@store/projectStore";

/** Snap a date to the most recent Monday (or same day if already Monday) */
function toMonday(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun, 1=Mon, ...6=Sat
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function toIsoDate(date: Date): string {
  // Use local date parts to avoid UTC timezone shift (toISOString() converts to UTC)
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function secondsToIsoDate(seconds: number): string {
  return toIsoDate(toMonday(new Date(seconds * 1000)));
}

function buildInitialSections(partners: PartnerInfos[]): PlanningSection[] {
  return PLANNING_SECTIONS.map((sectionDef) => ({
    section_key: sectionDef.key,
    artisan_ids: partners
      .filter((p) => p.company_type === sectionDef.key)
      .map((p) => p.id),
    steps: sectionDef.steps.map((step) => ({
      step_key: step.key,
      colored_weeks: {},
    })),
  }));
}

const MODEL_WEEKS: Record<PlanningModel, number> = {
  "11_mois": 48,
  "12_mois": 52,
  "12_mois_ordre": 52,
  "13_mois_avec_conges": 56,
  "14_mois_avec_conges": 60,
  "15_mois_avec_conges": 64,
};

function getYearsForPlanning(startDateIso: string, model: PlanningModel): number[] {
  const start = new Date(startDateIso);
  const weeks = MODEL_WEEKS[model];
  const end = new Date(start.getTime() + weeks * 7 * 24 * 60 * 60 * 1000);
  const startYear = start.getFullYear();
  const endYear = end.getFullYear();
  const years: number[] = [];
  for (let y = startYear; y <= endYear; y++) years.push(y);
  return years;
}

function injectDecennalesIntoProject(
  project: Project,
  partners: PartnerInfos[],
  years: number[]
): { updatedFolders: ProjectFolder[]; missingWarnings: string[] } {
  const folders: ProjectFolder[] = project.folders ? [...project.folders] : [];
  const missingWarnings: string[] = [];

  const partnersWithDecennales = partners.filter(
    (p) => p.decennales && p.decennales.length > 0
  );

  if (partnersWithDecennales.length === 0 && partners.length > 0) {
    missingWarnings.push(
      "Aucun intervenant n'a de décennale enregistrée. Pensez à les ajouter dans les fiches intervenants."
    );
    return { updatedFolders: folders, missingWarnings };
  }

  // Find or create "Décennales" top-level folder
  let decennalesFolder: ProjectFolder = folders.find((f) => f.name === "Décennales") ?? {
    id: `folder_decennales_${Date.now()}`,
    name: "Décennales",
    documents: [],
    sub_folders: [],
  };

  const subFolders: ProjectSubFolder[] = decennalesFolder.sub_folders
    ? [...decennalesFolder.sub_folders]
    : [];

  for (const partner of partners) {
    const partnerName = partner.company_name || partner.display_name;
    const decennales = partner.decennales || [];

    // Check for missing years
    for (const year of years) {
      if (!decennales.some((d) => d.year === year)) {
        missingWarnings.push(`${partnerName} — décennale ${year} manquante`);
      }
    }

    // Only create sub-folder if partner has at least one décennale for needed years
    const matchingDecennales = decennales.filter((d) => years.includes(d.year));
    if (matchingDecennales.length === 0) continue;

    let partnerSubFolder = subFolders.find((sf) => sf.name === partnerName);
    if (!partnerSubFolder) {
      partnerSubFolder = {
        id: `subfolder_dec_${partner.id}_${Date.now()}`,
        name: partnerName,
        documents: [],
      };
      subFolders.push(partnerSubFolder);
    }

    const existingDocs = partnerSubFolder.documents || [];
    for (const dec of matchingDecennales) {
      if (existingDocs.some((d) => d.name === `Décennale ${dec.year}.pdf`)) continue;
      const doc: ProjectDocument = {
        id: `doc_dec_${partner.id}_${dec.year}_${Date.now()}`,
        name: `Décennale ${dec.year}.pdf`,
        file: {
          id: `file_dec_${partner.id}_${dec.year}`,
          name: `Décennale ${dec.year}.pdf`,
          octets: 0,
          url: dec.url || dec.gcs_path,
          created_at: null as any,
          type: "pdf",
        },
      };
      existingDocs.push(doc);
    }
    partnerSubFolder = { ...partnerSubFolder, documents: existingDocs };
    const sfIdx = subFolders.findIndex((sf) => sf.name === partnerName);
    if (sfIdx >= 0) subFolders[sfIdx] = partnerSubFolder;
  }

  decennalesFolder = { ...decennalesFolder, sub_folders: subFolders };
  const folderIdx = folders.findIndex((f) => f.name === "Décennales");
  if (folderIdx >= 0) {
    folders[folderIdx] = decennalesFolder;
  } else {
    folders.push(decennalesFolder);
  }

  return { updatedFolders: folders, missingWarnings };
}

const modelOptions = [
  {
    value: "11_mois",
    title: "11 mois avec congés",
    description: "48 semaines — de Janvier à Novembre",
  },
  {
    value: "12_mois",
    title: "12 mois avec congés",
    description: "52 semaines — d'Octobre à Septembre (année suivante)",
  },
  {
    value: "12_mois_ordre",
    title: "12 mois ordre strict",
    description: "52 semaines — d'Octobre à Septembre (sans congés)",
  },
  {
    value: "13_mois_avec_conges",
    title: "13 mois avec congés",
    description: "56 semaines — de Janvier à Janvier (année suivante)",
  },
  {
    value: "14_mois_avec_conges",
    title: "14 mois avec congés",
    description: "60 semaines — de Janvier à Février (année +1)",
  },
  {
    value: "15_mois_avec_conges",
    title: "15 mois avec congés",
    description: "64 semaines — de Janvier à Mars (année +1)",
  },
];

interface Props {
  onClose: () => void;
  onCreated?: (planning: Planning) => void;
  onUpdated?: (planning: Planning) => void;
  onProjectUpdated?: (project: Project) => void;
  projectId: string;
  orgId: string;
  project: Project;
  partners: PartnerInfos[];
  existingPlanning?: Planning;
}

type Step = "model" | "sections";

export const PlanningSetupModal = ({
  onClose,
  onCreated,
  onUpdated,
  onProjectUpdated,
  projectId,
  orgId,
  project,
  partners,
  existingPlanning,
}: Props) => {
  const isEditing = !!existingPlanning;

  const [step, setStep] = useState<Step>("model");
  const [model, setModel] = useState<PlanningModel>(
    existingPlanning?.model ?? "11_mois"
  );
  const [startDateSeconds, setStartDateSeconds] = useState<number>(
    existingPlanning
      ? Math.floor(new Date(existingPlanning.start_date).getTime() / 1000)
      : Math.floor(toMonday(new Date()).getTime() / 1000)
  );
  const [sections, setSections] = useState<PlanningSection[]>(
    existingPlanning?.sections ?? buildInitialSections(partners)
  );
  const [saving, setSaving] = useState(false);

  function toggleArtisan(sectionKey: string, artisanId: string) {
    setSections((prev) =>
      prev.map((s) => {
        if (s.section_key !== sectionKey) return s;
        const has = s.artisan_ids.includes(artisanId);
        return {
          ...s,
          artisan_ids: has
            ? s.artisan_ids.filter((id) => id !== artisanId)
            : [...s.artisan_ids, artisanId],
        };
      })
    );
  }

  async function handleSave() {
    setSaving(true);
    try {
      if (isEditing && existingPlanning) {
        const planning = await planningsApi.update(existingPlanning.id, {
          model,
          start_date: secondsToIsoDate(startDateSeconds),
          sections,
        });
        showNotification({ type: "success", message: "Planning modifié avec succès" });
        onUpdated?.(planning);
      } else {
        const startIso = secondsToIsoDate(startDateSeconds);
        const planning = await planningsApi.create({
          project_id: projectId,
          org_id: orgId,
          model,
          start_date: startIso,
          sections,
        });

        // Inject décennales into project folders
        const years = getYearsForPlanning(startIso, model);
        // Enrich project-embedded partners with org-level partner data (which carries decennales)
        const orgPartners = useProjectStore.getState().partners;
        const enrichedPartners = partners.map((p) => {
          const orgPartner = orgPartners.find((op) => op.id === p.id);
          return orgPartner ? { ...p, decennales: orgPartner.decennales } : p;
        });
        const { updatedFolders, missingWarnings } = injectDecennalesIntoProject(
          project,
          enrichedPartners,
          years
        );

        if (missingWarnings.length > 0) {
          missingWarnings.forEach((w) =>
            showNotification({ type: "error", message: w })
          );
        }

        const updatedProject: Project = { ...project, folders: updatedFolders };
        await projectsApi.update(projectId, { folders: updatedFolders as any });
        onProjectUpdated?.(updatedProject);

        showNotification({ type: "success", message: "Planning créé avec succès" });
        onCreated?.(planning);
      }
      onClose();
    } catch {
      showNotification({
        type: "error",
        message: isEditing
          ? "Erreur lors de la modification du planning"
          : "Erreur lors de la création du planning",
      });
    } finally {
      setSaving(false);
    }
  }

  const isoDate = secondsToIsoDate(startDateSeconds);

  return (
    <BaseModal
      onClose={onClose}
      title={isEditing ? "Modifier le planning" : "Créer un planning prévisionnel"}
      startIcon={<Icon1 name="planning" size={20} />}
      width="max-w-2xl"
    >
      <div className="flex flex-col gap-6 p-6">
        {step === "model" && (
          <>
            <CardSelection
              label="Modèle de planning"
              subtitle="Choisissez la durée du chantier."
              options={modelOptions}
              value={model}
              onChange={(v) => setModel(v as PlanningModel)}
            />

            <DatePicker
              label="Date de démarrage (semaine)"
              seconds={startDateSeconds}
              onChange={(s) => setStartDateSeconds(s)}
            />

            {startDateSeconds > 0 && (
              <div className="text-h3 text-neutral-500 bg-neutral-100 rounded-xl px-4 py-2">
                Démarrage le lundi <strong>{isoDate}</strong>
              </div>
            )}

            <div className="flex justify-end">
              <Button
                label="Suivant — Artisans"
                onClick={() => setStep("sections")}
              />
            </div>
          </>
        )}

        {step === "sections" && (
          <>
            <div className="text-h2 font-medium text-neutral-700">
              Assigner les artisans par corps de métier
            </div>
            <div className="text-h3 text-neutral-400">
              Sélectionnez les intervenants du projet pour chaque section du planning.
            </div>

            <div className="flex flex-col gap-4 max-h-[50vh] overflow-y-auto">
              {PLANNING_SECTIONS.map((sectionDef) => {
                const sectionState = sections.find(
                  (s) => s.section_key === sectionDef.key
                );
                const selectedIds = sectionState?.artisan_ids || [];

                return (
                  <div key={sectionDef.key} className="flex flex-col gap-2">
                    <div
                      className={`px-3 py-1 rounded-lg text-h3 font-bold ${sectionDef.headerBg} ${sectionDef.headerText}`}
                    >
                      {sectionDef.label}
                    </div>
                    {partners.length === 0 ? (
                      <div className="text-h4 text-neutral-400 pl-2">
                        Aucun intervenant sur ce projet
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2 pl-2">
                        {partners.map((partner) => {
                          const isSelected = selectedIds.includes(partner.id);
                          return (
                            <button
                              key={partner.id}
                              onClick={() =>
                                toggleArtisan(sectionDef.key, partner.id)
                              }
                              className={`px-3 py-1 rounded-full text-h3 border transition-all ${
                                isSelected
                                  ? "bg-neutral-900 text-white border-neutral-900"
                                  : "bg-white text-neutral-700 border-neutral-300 hover:border-neutral-500"
                              }`}
                            >
                              {partner.company_name || partner.display_name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex justify-between">
              <button
                className="text-h3 text-neutral-400 hover:text-neutral-600"
                onClick={() => setStep("model")}
              >
                ← Retour
              </button>
              <Button
                label={saving ? "Enregistrement..." : isEditing ? "Modifier le planning" : "Créer le planning"}
                onClick={handleSave}
              />
            </div>
          </>
        )}
      </div>
    </BaseModal>
  );
};
